import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Sparkle, Check, Image as ImageIcon, CircleNotch, Rocket, Eye, Globe,
  Plus, Trash, LinkSimple, FilmSlate, NotePencil, ArrowsLeftRight, MagnifyingGlass,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LocationPicker from "@/components/LocationPicker";

const STEPS = ["Classify", "Details", "Modules", "Theme", "Content", "Languages", "Publish"];

function Chips({ options, selected = [], onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = selected.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => onToggle(o)}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${active ? "border-secondary bg-secondary text-secondary-foreground" : "border-border bg-background text-muted-foreground hover:border-secondary/50"}`}
          >
            {active && <Check size={13} className="mr-1 inline" />}{o}
          </button>
        );
      })}
    </div>
  );
}

export default function SiteBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [taxo, setTaxo] = useState(null);
  const [inst, setInst] = useState(null);
  const [step, setStep] = useState(0);
  const [cls, setCls] = useState({ subtypes: [], education_levels: [], characteristics: [], delivery_modes: [], local_classification: {} });
  const [details, setDetails] = useState({});
  const [site, setSite] = useState({ modules: [], theme: "scholar", sections: {} });
  const [readiness, setReadiness] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transBusy, setTransBusy] = useState(null);
  const [otherLang, setOtherLang] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveQuery, setMoveQuery] = useState("");
  const [moveResults, setMoveResults] = useState([]);
  const [moving, setMoving] = useState(false);
  const logoRef = useRef();
  const photoRef = useRef();

  useEffect(() => {
    api.get("/taxonomy").then((r) => setTaxo(r.data));
    api.get(`/institutions/${id}`).then((r) => {
      const d = r.data;
      if (!d.is_manager) { toast.error("Only managers can build this website"); nav(`/institutions/${id}`); return; }
      setInst(d);
      setCls({
        category: d.category, subtypes: d.subtypes || [], education_levels: d.education_levels || [],
        characteristics: d.characteristics || [], ownership: d.ownership, delivery_modes: d.delivery_modes || [],
        gender_model: d.gender_model, status: d.status || "Active", local_classification: d.local_classification || {},
      });
      setDetails({ description: d.description || "", location: d.location || "", country: d.country || "", state: d.state || "", district: d.district || "", city: d.city || "", website: d.website || "", founded: d.founded || "", logo_url: d.logo_url || "", departments: d.departments || [] });
      setSite({ modules: d.site?.modules || [], theme: d.site?.theme || "scholar", sections: d.site?.sections || {}, tagline: d.site?.tagline, notes: d.site?.notes || "", slug: d.site?.slug, published: d.site?.published, languages: d.site?.languages || ["en"], default_lang: d.site?.default_lang || "en", translations: d.site?.translations || {} });
    }).catch(() => { toast.error("Institution not found"); nav("/institutions"); });
  }, [id, nav]);

  if (!taxo || !inst) return <div className="p-12 text-center text-muted-foreground">Loading builder…</div>;

  const catObj = taxo.categories.find((c) => c.value === cls.category);
  const countryTerms = cls.local_classification?.country ? (taxo.country_terms[cls.local_classification.country] || []) : [];

  const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const saveClassification = async () => {
    setSaving(true);
    try { await api.put(`/institutions/${id}/classification`, cls); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const saveDetails = async () => {
    setSaving(true);
    try { await api.put(`/institutions/${id}`, details); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const saveSite = async (patch) => {
    const next = { ...site, ...patch };
    setSite(next);
    try { await api.put(`/institutions/${id}/site`, { theme: next.theme, modules: next.modules, sections: next.sections, tagline: next.tagline, notes: next.notes }); }
    catch (e) { toast.error("Could not save"); }
  };
  const saveSlug = async () => {
    try {
      const { data } = await api.put(`/institutions/${id}/site`, { slug: site.slug || "" });
      setSite((s) => ({ ...s, slug: data.site.slug }));
      toast.success("Custom URL saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // rich media helpers (stored inside site.sections)
  const galleryArr = site.sections?.gallery || [];
  const linksArr = site.sections?.links || [];
  const mediaArr = site.sections?.media || [];
  const setSecArr = (key, arr) => setSite((s) => ({ ...s, sections: { ...s.sections, [key]: arr } }));
  const uploadPhoto = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setSecArr("gallery", [...galleryArr, { file_id: data.id, caption: "" }]); toast.success("Photo added"); }
    catch { toast.error("Upload failed"); }
  };
  // move-site tool
  const searchMove = async (q) => {
    setMoveQuery(q);
    if (q.trim().length < 2) { setMoveResults([]); return; }
    try { const { data } = await api.get("/public/search", { params: { q: q.trim(), limit: 8 } }); setMoveResults((data.institutions || []).filter((x) => x.id !== id)); } catch {}
  };
  const doMove = async (target) => {
    if (!window.confirm(`Move this site to "${target.name}"? The public URL will change to match that institution and this one will no longer host the site.`)) return;
    setMoving(true);
    try {
      const { data } = await api.post(`/institutions/${id}/site/move`, { target_inst_id: target.id });
      toast.success("Site moved — redirecting to the correct institution builder");
      setMoveOpen(false);
      nav(`/institutions/${data.target_inst_id}/build`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMoving(false); }
  };
  const uploadLogo = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setDetails((d) => ({ ...d, logo_url: fileUrl(data.id) })); toast.success("Logo uploaded"); }
    catch { toast.error("Upload failed"); }
  };
  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/institutions/${id}/site/generate`);
      setSite((s) => ({ ...s, sections: data.site.sections, tagline: data.site.tagline, modules: s.modules.length ? s.modules : data.site.modules }));
      toast.success("AI drafted your homepage");
    } catch (e) { toast.error("AI generation failed — you can still edit manually"); }
    finally { setGenerating(false); }
  };
  const publish = async () => {
    setSaving(true);
    try {
      await saveSite({});
      const { data } = await api.post(`/institutions/${id}/site/publish?publish=true`);
      setReadiness(data.readiness);
      setSite((s) => ({ ...s, slug: data.site.slug, published: true }));
      toast.success("Website published!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const loadReadiness = async () => {
    try { const { data } = await api.get(`/institutions/${id}/readiness`); setReadiness(data); } catch {}
  };

  const langOptions = taxo.languages;
  const langLabel = (code) => (langOptions.find((l) => l.code === code)?.label) || code;

  const toggleLang = (code) => {
    setSite((s) => {
      const has = (s.languages || []).includes(code);
      let langs = has ? s.languages.filter((x) => x !== code) : [...(s.languages || []), code];
      if (!langs.includes(s.default_lang)) langs = [s.default_lang, ...langs];
      return { ...s, languages: langs };
    });
  };
  const addOtherLang = () => {
    const code = otherLang.trim();
    if (!code) return;
    setSite((s) => (s.languages.includes(code) ? s : { ...s, languages: [...s.languages, code] }));
    setOtherLang("");
  };
  const saveLanguages = async () => {
    try { await api.put(`/institutions/${id}/site/languages`, { default_lang: site.default_lang, languages: site.languages }); }
    catch (e) { toast.error("Could not save languages"); }
  };
  const translateLang = async (code) => {
    setTransBusy(code);
    try {
      await saveSite({});
      const { data } = await api.post(`/institutions/${id}/site/translate?lang=${encodeURIComponent(code)}`);
      setSite((s) => ({ ...s, translations: { ...s.translations, [code]: data.translation } }));
      toast.success(`Translated to ${langLabel(code)}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Translation failed"); }
    finally { setTransBusy(null); }
  };
  const editTrans = (code, path, val) => {
    setSite((s) => {
      const tr = { ...(s.translations[code] || { sections: {} }) };
      if (path === "tagline") tr.tagline = val;
      else {
        const [sec, field] = path.split(".");
        tr.sections = { ...(tr.sections || {}), [sec]: { ...((tr.sections || {})[sec] || {}), [field]: val } };
      }
      return { ...s, translations: { ...s.translations, [code]: tr } };
    });
  };
  const saveTrans = async (code) => {
    const tr = site.translations[code] || {};
    try { await api.put(`/institutions/${id}/site/translation?lang=${encodeURIComponent(code)}`, { tagline: tr.tagline, sections: tr.sections }); toast.success("Translation saved"); }
    catch (e) { toast.error("Could not save translation"); }
  };

  const next = async () => {
    if (step === 0) await saveClassification();
    if (step === 1) await saveDetails();
    if (step === 2 || step === 3 || step === 4) await saveSite({});
    if (step === 5) { await saveLanguages(); await loadReadiness(); }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const setModules = (m) => setSite((s) => ({ ...s, modules: toggle(s.modules, m) }));
  const recommended = taxo.recommended_modules[cls.category] || [];
  const useRecommended = () => setSite((s) => ({ ...s, modules: recommended }));
  const setSection = (key, field, val) => setSite((s) => ({ ...s, sections: { ...s.sections, [key]: { ...(s.sections[key] || {}), [field]: val } } }));

  return (
    <main className="mx-auto max-w-3xl px-5 py-8" data-testid="site-builder">
      <button onClick={() => nav(`/institutions/${id}`)} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> {inst.name}</button>

      <div className="mb-6">
        <h1 className="font-heading text-3xl font-medium">Website builder</h1>
        <p className="mt-1 text-sm text-muted-foreground">Turn {inst.name} into a professional public website in a few steps.</p>
        <div className="mt-4">
          <Progress value={((step + 1) / STEPS.length) * 100} className="h-2" />
          <div className="mt-2 flex justify-between text-xs">
            {STEPS.map((s, i) => (
              <span key={s} className={i <= step ? "font-medium text-secondary" : "text-muted-foreground"}>{s}</span>
            ))}
          </div>
        </div>
      </div>

      <Card className="border-border/60 p-6 shadow-sm">
        {step === 0 && (
          <div className="space-y-6" data-testid="step-classify">
            <div>
              <Label>Primary category</Label>
              <Select value={cls.category} onValueChange={(v) => setCls({ ...cls, category: v, subtypes: [] })}>
                <SelectTrigger className="mt-1.5" data-testid="category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent>{taxo.categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {catObj && (
              <div><Label>Subtype(s)</Label><div className="mt-1.5"><Chips testid="subtype-chips" options={catObj.subtypes} selected={cls.subtypes} onToggle={(v) => setCls({ ...cls, subtypes: toggle(cls.subtypes, v) })} /></div></div>
            )}
            <div><Label>Education levels</Label><div className="mt-1.5"><Chips options={taxo.education_levels} selected={cls.education_levels} onToggle={(v) => setCls({ ...cls, education_levels: toggle(cls.education_levels, v) })} /></div></div>
            <div><Label>Characteristics</Label><div className="mt-1.5"><Chips options={taxo.characteristics} selected={cls.characteristics} onToggle={(v) => setCls({ ...cls, characteristics: toggle(cls.characteristics, v) })} /></div></div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div><Label>Ownership</Label>
                <Select value={cls.ownership} onValueChange={(v) => setCls({ ...cls, ownership: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="ownership-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{taxo.ownership_types.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Gender model</Label>
                <Select value={cls.gender_model} onValueChange={(v) => setCls({ ...cls, gender_model: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{taxo.gender_models.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Status</Label>
                <Select value={cls.status} onValueChange={(v) => setCls({ ...cls, status: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{taxo.statuses.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label>Delivery mode</Label><div className="mt-1.5"><Chips options={taxo.delivery_modes} selected={cls.delivery_modes} onToggle={(v) => setCls({ ...cls, delivery_modes: toggle(cls.delivery_modes, v) })} /></div></div>
            <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
              <Label>Country-specific classification</Label>
              <p className="mb-2 mt-0.5 text-xs text-muted-foreground">We store both a global standard and your local terminology.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Select value={cls.local_classification?.country} onValueChange={(v) => setCls({ ...cls, local_classification: { country: v } })}>
                  <SelectTrigger data-testid="country-select"><SelectValue placeholder="Country" /></SelectTrigger>
                  <SelectContent>{Object.keys(taxo.country_terms).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                {countryTerms.length > 0 && (
                  <Select value={cls.local_classification?.local_type} onValueChange={(v) => { const t = countryTerms.find((x) => x.local === v); setCls({ ...cls, local_classification: { ...cls.local_classification, local_type: v, global: t?.global } }); }}>
                    <SelectTrigger><SelectValue placeholder="Local type" /></SelectTrigger>
                    <SelectContent>{countryTerms.map((t) => <SelectItem key={t.local} value={t.local}>{t.local} → {t.global}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5" data-testid="step-details">
            <div className="flex items-center gap-4">
              <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-xl border border-border bg-muted">
                {details.logo_url ? <img src={details.logo_url} alt="logo" className="h-full w-full object-cover" /> : <ImageIcon size={26} className="text-muted-foreground" />}
              </div>
              <div>
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => logoRef.current.click()} data-testid="upload-logo-btn">Upload logo</Button>
                <input ref={logoRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} />
                <p className="mt-1 text-xs text-muted-foreground">Square image works best.</p>
              </div>
            </div>
            <div><Label>Description</Label><Textarea rows={4} className="mt-1.5" value={details.description} onChange={(e) => setDetails({ ...details, description: e.target.value })} placeholder="What is this institution known for?" data-testid="description-input" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Location (display address)</Label><Input className="mt-1.5" value={details.location} onChange={(e) => setDetails({ ...details, location: e.target.value })} placeholder="e.g. Elivaly, Kottayam, Kerala" /></div>
              <div><Label>Website</Label><Input className="mt-1.5" value={details.website} onChange={(e) => setDetails({ ...details, website: e.target.value })} placeholder="https://" /></div>
              <div><Label>Founded</Label><Input className="mt-1.5" value={details.founded} onChange={(e) => setDetails({ ...details, founded: e.target.value })} placeholder="e.g. 1989" /></div>
            </div>
            <LocationPicker value={{ country: details.country, state: details.state, district: details.district, city: details.city }}
              onChange={(loc) => setDetails({ ...details, ...loc })} />
            <div><Label>Departments (comma separated)</Label><Input className="mt-1.5" value={(details.departments || []).join(", ")} onChange={(e) => setDetails({ ...details, departments: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Computer Science, Mechanical" data-testid="departments-input" /></div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="step-modules">
            <div className="flex items-center justify-between">
              <div><Label>Website sections</Label><p className="text-xs text-muted-foreground">Choose which pages appear on your site.</p></div>
              <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={useRecommended} data-testid="use-recommended-btn"><Sparkle size={14} /> Use recommended</Button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {taxo.modules.map((m) => {
                const active = site.modules.includes(m.value);
                const rec = recommended.includes(m.value);
                return (
                  <button key={m.value} type="button" onClick={() => setModules(m.value)} className={`flex items-center justify-between rounded-xl border px-3 py-2.5 text-sm transition-colors ${active ? "border-secondary bg-secondary/10" : "border-border hover:border-secondary/40"}`} data-testid={`module-${m.value}`}>
                    <span>{m.label}{rec && <span className="ml-1 text-[10px] text-secondary">•</span>}</span>
                    {active && <Check size={15} className="text-secondary" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="step-theme">
            <div><Label>Choose a theme</Label><p className="text-xs text-muted-foreground">You can change this anytime — it never affects your content.</p></div>
            <div className="grid gap-4 sm:grid-cols-3">
              {taxo.themes.map((t) => (
                <button key={t.value} type="button" onClick={() => setSite({ ...site, theme: t.value })} className={`rounded-2xl border-2 p-4 text-left transition-all ${site.theme === t.value ? "border-secondary shadow-md" : "border-border hover:border-secondary/40"}`} data-testid={`theme-${t.value}`}>
                  <div className={`mb-3 h-16 rounded-lg ${t.value === "scholar" ? "bg-[#1f3d33]" : t.value === "campus" ? "bg-amber-500" : "bg-teal-600"}`} />
                  <div className="font-heading text-base font-medium">{t.label}</div>
                  <div className="text-xs text-muted-foreground">{t.description}</div>
                  <div className="mt-1 text-[11px] text-secondary">Best for {t.for}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5" data-testid="step-content">
            <div className="flex items-center justify-between rounded-xl border border-secondary/30 bg-secondary/5 p-4">
              <div><p className="font-medium">Let AI draft your homepage</p><p className="text-xs text-muted-foreground">Generates a tagline, hero and about copy from your data. No invented facts.</p></div>
              <Button onClick={generate} disabled={generating} className="rounded-full gap-1.5" data-testid="ai-generate-btn">
                {generating ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" />} {generating ? "Writing…" : "Generate"}
              </Button>
            </div>
            <div><Label>Tagline</Label><Input className="mt-1.5" value={site.tagline || ""} onChange={(e) => setSite({ ...site, tagline: e.target.value })} data-testid="tagline-input" /></div>
            <div><Label>Hero headline</Label><Input className="mt-1.5" value={site.sections?.hero?.headline || ""} onChange={(e) => setSection("hero", "headline", e.target.value)} data-testid="hero-headline-input" /></div>
            <div><Label>Hero subtext</Label><Textarea rows={2} className="mt-1.5" value={site.sections?.hero?.subtext || ""} onChange={(e) => setSection("hero", "subtext", e.target.value)} /></div>
            <div><Label>About</Label><Textarea rows={5} className="mt-1.5" value={site.sections?.about?.body || ""} onChange={(e) => setSection("about", "body", e.target.value)} data-testid="about-input" /></div>

            {/* Photos */}
            <div className="rounded-xl border border-border/60 p-4" data-testid="content-photos">
              <div className="mb-2 flex items-center justify-between">
                <Label>Photos</Label>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={() => photoRef.current.click()} data-testid="add-photo-btn"><Plus size={14} /> Add photo</Button>
                <input ref={photoRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
              </div>
              {galleryArr.length === 0 ? <p className="text-xs text-muted-foreground">Upload campus or memory photos to show in a gallery.</p> : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {galleryArr.map((g, i) => (
                    <div key={i} className="group relative">
                      <img src={g.file_id ? fileUrl(g.file_id) : g.url} alt="" className="aspect-square w-full rounded-lg object-cover" />
                      <button onClick={() => setSecArr("gallery", galleryArr.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100" data-testid={`remove-photo-${i}`}><Trash size={12} /></button>
                      <Input value={g.caption || ""} onChange={(e) => setSecArr("gallery", galleryArr.map((x, j) => j === i ? { ...x, caption: e.target.value } : x))} placeholder="Caption" className="mt-1 h-7 text-xs" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Video & audio links */}
            <div className="rounded-xl border border-border/60 p-4" data-testid="content-media">
              <div className="mb-2 flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><FilmSlate size={15} /> Video & audio links</Label>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={() => setSecArr("media", [...mediaArr, { type: "video", label: "", url: "" }])} data-testid="add-media-btn"><Plus size={14} /> Add</Button>
              </div>
              {mediaArr.length === 0 ? <p className="text-xs text-muted-foreground">Paste a YouTube/Vimeo link (embeds) or an audio file link.</p> : (
                <div className="space-y-2">
                  {mediaArr.map((m, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`media-row-${i}`}>
                      <Select value={m.type} onValueChange={(v) => setSecArr("media", mediaArr.map((x, j) => j === i ? { ...x, type: v } : x))}>
                        <SelectTrigger className="h-9 w-28"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="video">Video</SelectItem><SelectItem value="audio">Audio</SelectItem></SelectContent>
                      </Select>
                      <Input value={m.label || ""} onChange={(e) => setSecArr("media", mediaArr.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Label" className="h-9 w-36" />
                      <Input value={m.url || ""} onChange={(e) => setSecArr("media", mediaArr.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} placeholder="https://…" className="h-9 flex-1 min-w-[180px]" />
                      <button onClick={() => setSecArr("media", mediaArr.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Web links */}
            <div className="rounded-xl border border-border/60 p-4" data-testid="content-links">
              <div className="mb-2 flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><LinkSimple size={15} /> Website & page links</Label>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={() => setSecArr("links", [...linksArr, { label: "", url: "" }])} data-testid="add-link-btn"><Plus size={14} /> Add</Button>
              </div>
              {linksArr.length === 0 ? <p className="text-xs text-muted-foreground">Add links to official pages, news articles, social profiles, etc.</p> : (
                <div className="space-y-2">
                  {linksArr.map((l, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`link-row-${i}`}>
                      <Input value={l.label || ""} onChange={(e) => setSecArr("links", linksArr.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Label" className="h-9 w-40" />
                      <Input value={l.url || ""} onChange={(e) => setSecArr("links", linksArr.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} placeholder="https://…" className="h-9 flex-1 min-w-[180px]" />
                      <button onClick={() => setSecArr("links", linksArr.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div><Label className="flex items-center gap-1.5"><NotePencil size={15} /> Notes / Comments</Label><Textarea rows={3} className="mt-1.5" value={site.notes || ""} onChange={(e) => setSite({ ...site, notes: e.target.value })} placeholder="Free-text notes shown on the site (editable by managers/admins)." data-testid="notes-input" /></div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-6" data-testid="step-languages">
            <div>
              <Label>Default language</Label>
              <p className="text-xs text-muted-foreground">The language your original content is written in.</p>
              <Select value={site.default_lang} onValueChange={(v) => setSite((s) => ({ ...s, default_lang: v, languages: s.languages.includes(v) ? s.languages : [v, ...s.languages] }))}>
                <SelectTrigger className="mt-1.5 max-w-xs" data-testid="default-lang-select"><SelectValue /></SelectTrigger>
                <SelectContent>{langOptions.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Offer your site in these languages</Label>
              <p className="mb-2 text-xs text-muted-foreground">Visitors can switch languages on your public site.</p>
              <div className="flex flex-wrap gap-2" data-testid="lang-chips">
                {langOptions.map((l) => {
                  const active = (site.languages || []).includes(l.code);
                  const isDefault = l.code === site.default_lang;
                  return (
                    <button key={l.code} type="button" disabled={isDefault} onClick={() => toggleLang(l.code)} className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${active ? "border-secondary bg-secondary text-secondary-foreground" : "border-border bg-background text-muted-foreground hover:border-secondary/50"} ${isDefault ? "opacity-60" : ""}`}>
                      {active && <Check size={13} className="mr-1 inline" />}{l.label}{isDefault && " (default)"}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Input value={otherLang} onChange={(e) => setOtherLang(e.target.value)} placeholder="Other language (e.g. Swahili)" className="max-w-xs" data-testid="other-lang-input" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOtherLang())} />
                <Button variant="outline" size="sm" className="rounded-full" onClick={addOtherLang} data-testid="add-other-lang">Add</Button>
              </div>
            </div>

            {(site.languages || []).filter((c) => c !== site.default_lang).map((code) => {
              const tr = site.translations?.[code] || {};
              return (
                <div key={code} className="rounded-2xl border border-border/60 p-4" data-testid={`trans-card-${code}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-base font-medium">{langLabel(code)}</span>
                    <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => translateLang(code)} disabled={transBusy === code} data-testid={`translate-${code}`}>
                      {transBusy === code ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} />} {tr.tagline || tr.sections ? "Re-translate" : "AI Translate"}
                    </Button>
                  </div>
                  <div className="mt-3 space-y-3">
                    <div><Label className="text-xs">Tagline</Label><Input className="mt-1" value={tr.tagline || ""} onChange={(e) => editTrans(code, "tagline", e.target.value)} /></div>
                    <div><Label className="text-xs">Hero headline</Label><Input className="mt-1" value={tr.sections?.hero?.headline || ""} onChange={(e) => editTrans(code, "hero.headline", e.target.value)} data-testid={`trans-hero-${code}`} /></div>
                    <div><Label className="text-xs">Hero subtext</Label><Textarea rows={2} className="mt-1" value={tr.sections?.hero?.subtext || ""} onChange={(e) => editTrans(code, "hero.subtext", e.target.value)} /></div>
                    <div><Label className="text-xs">About</Label><Textarea rows={4} className="mt-1" value={tr.sections?.about?.body || ""} onChange={(e) => editTrans(code, "about.body", e.target.value)} /></div>
                    <p className="text-[11px] text-muted-foreground">Department names, course names & faculty titles are translated automatically when you run AI Translate.</p>
                    <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => saveTrans(code)} data-testid={`save-trans-${code}`}><Check size={14} /> Save {langLabel(code)}</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {step === 6 && (
          <div className="space-y-5" data-testid="step-publish">
            {readiness && (
              <div className="rounded-2xl border border-border/60 p-5">
                <div className="flex items-center justify-between">
                  <span className="font-heading text-lg font-medium">Website readiness</span>
                  <span className={`font-heading text-2xl font-semibold ${readiness.score >= 80 ? "text-secondary" : readiness.score >= 50 ? "text-amber-600" : "text-destructive"}`} data-testid="readiness-score">{readiness.score}%</span>
                </div>
                <Progress value={readiness.score} className="mt-3 h-2" />
                {readiness.todo?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-muted-foreground">To improve your score:</p>
                    <ul className="mt-1.5 space-y-1">{readiness.todo.map((t) => <li key={t} className="text-sm text-muted-foreground">○ {t}</li>)}</ul>
                  </div>
                )}
              </div>
            )}
            <div className="rounded-2xl bg-primary p-6 text-center text-primary-foreground">
              <Rocket size={30} className="mx-auto" weight="duotone" />
              <p className="mt-2 font-heading text-xl">{site.published ? "Your site is live" : "Ready to launch"}</p>
              {site.published && site.slug && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                  <Link to={`/site/${site.slug}`} target="_blank"><Button variant="secondary" size="sm" className="rounded-full gap-1.5" data-testid="view-live-site"><Eye size={15} /> View site</Button></Link>
                  <span className="rounded-full bg-white/15 px-3 py-1 text-xs">/site/{site.slug}</span>
                </div>
              )}
              <Button onClick={publish} disabled={saving} className="mt-4 rounded-full bg-white text-primary hover:bg-white/90 gap-1.5" data-testid="publish-btn">
                {saving ? <CircleNotch size={15} className="animate-spin" /> : <Globe size={15} />} {site.published ? "Re-publish changes" : "Publish website"}
              </Button>
            </div>

            {/* Custom URL */}
            <div className="rounded-2xl border border-border/60 p-5" data-testid="custom-url-card">
              <Label className="flex items-center gap-1.5"><Globe size={15} /> Custom URL (slug)</Label>
              <p className="mb-2 mt-0.5 text-xs text-muted-foreground">Your public web address. Use your school/college name, e.g. <code>st-georges-lower-primary-school-elivaly</code>.</p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">/site/</span>
                <Input value={site.slug || ""} onChange={(e) => setSite({ ...site, slug: e.target.value })} placeholder="your-institution-name" className="max-w-sm" data-testid="slug-input" />
                <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={saveSlug} data-testid="save-slug-btn"><Check size={14} /> Save URL</Button>
              </div>
            </div>

            {/* Move / fix wrong institution */}
            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5" data-testid="move-site-card">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="flex items-center gap-1.5"><ArrowsLeftRight size={15} /> Wrong institution?</Label>
                  <p className="mt-0.5 text-xs text-muted-foreground">If this site is attached to the wrong school/college, move it to the correct one. The URL updates to match.</p>
                </div>
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => setMoveOpen((v) => !v)} data-testid="toggle-move-btn">{moveOpen ? "Cancel" : "Move site"}</Button>
              </div>
              {moveOpen && (
                <div className="mt-3">
                  <div className="relative">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={moveQuery} onChange={(e) => searchMove(e.target.value)} placeholder="Search the correct institution by name…" className="pl-9" data-testid="move-search-input" />
                  </div>
                  <div className="mt-2 max-h-64 space-y-1 overflow-y-auto">
                    {moveResults.map((r) => (
                      <button key={r.id} onClick={() => doMove(r)} disabled={moving} className="flex w-full items-center justify-between rounded-lg border border-border/60 bg-white px-3 py-2 text-left text-sm transition-colors hover:border-secondary/50" data-testid={`move-target-${r.id}`}>
                        <span><span className="font-medium">{r.name}</span><span className="ml-2 text-xs text-muted-foreground">{[r.type, r.location || r.country].filter(Boolean).join(" · ")}</span></span>
                        <ArrowRight size={14} />
                      </button>
                    ))}
                    {moveQuery.trim().length >= 2 && moveResults.length === 0 && <p className="px-1 py-2 text-xs text-muted-foreground">No matching institution found.</p>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between border-t border-border/60 pt-5">
          <Button variant="ghost" onClick={back} disabled={step === 0} className="gap-1.5" data-testid="builder-back"><ArrowLeft size={15} /> Back</Button>
          {step < STEPS.length - 1
            ? <Button onClick={next} disabled={saving} className="rounded-full gap-1.5" data-testid="builder-next">{saving ? "Saving…" : "Continue"} <ArrowRight size={15} /></Button>
            : <span className="text-xs text-muted-foreground">Publish above to go live</span>}
        </div>
      </Card>
    </main>
  );
}
